import {
    collection,
    doc,
    getDocs,
    Timestamp,
    writeBatch,
} from 'firebase/firestore';
import { firestore } from '../firebase';

/**
 * Watchlist News read-state service (W8 — Firestore side-store).
 *
 * Trevor's 2026-07-30 direction (see NEWS_FEED_LIVING_SPEC.md Decision Log)
 * routed read-state through Firestore with a native TTL policy rather than
 * localStorage or the main Postgres. This module owns the client-side
 * Firestore reads/writes for `users/{userId}/news_read_state/{articleId}` and
 * nothing else — the polling news feed itself (`newsService.js`,
 * `useWatchlistNews`) is unchanged.
 *
 * Doc shape:
 *   {
 *     user_id: string,        // matches the path segment; a defensive echo so
 *                              // an admin-side scan still self-attributes rows.
 *     article_id: string,     // `news_article.article_id` from the W3 feed.
 *     read_at: Timestamp,     // when the popover first opened for this article.
 *     expires_at: Timestamp,  // now + READ_STATE_TTL_DAYS; drives the native
 *                              // TTL sweep configured admin-side on this coll.
 *   }
 *
 * TTL: the native Firestore TTL policy is configured out-of-band (gcloud /
 * console; see W8 evidence log) on the `expires_at` field. Client code only
 * has to write the field; the sweep is enforced by Firestore itself. 90 days
 * is well past the largest recency window (48 hr) so a returning user still
 * sees consistent state; also cheap enough that we don't need per-write
 * bookkeeping.
 */

export const READ_STATE_TTL_DAYS = 90;
const READ_STATE_TTL_MS = READ_STATE_TTL_DAYS * 24 * 60 * 60 * 1000;

// Firestore's WriteBatch caps at 500 operations. Popovers currently render at
// most 10 articles, so a single batch covers any conceivable open-event even
// if the user hammers popovers across their watchlist.
export const READ_STATE_BATCH_LIMIT = 500;

const READ_STATE_COLLECTION = 'news_read_state';

function normalizeArticleId(raw) {
    if (raw === null || raw === undefined) return '';
    const text = String(raw).trim();
    return text || '';
}

function normalizeUserUid(raw) {
    if (raw === null || raw === undefined) return '';
    const text = String(raw).trim();
    return text || '';
}

/**
 * Load every read-state document for `userUid` and return a Set of article_ids.
 *
 * Returns an empty Set on any failure so the news-feed rendering path stays
 * usable when Firestore is unreachable or the user has never marked an article
 * as read. Failures are logged but not rethrown — read-state is a soft signal.
 */
export async function loadReadArticleIds(userUid) {
    const uid = normalizeUserUid(userUid);
    if (!uid) return new Set();
    try {
        const collRef = collection(firestore, 'users', uid, READ_STATE_COLLECTION);
        const snapshot = await getDocs(collRef);
        const ids = new Set();
        snapshot.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const fromField = normalizeArticleId(data.article_id);
            const fromId = normalizeArticleId(docSnap.id);
            const articleId = fromField || fromId;
            if (articleId) {
                ids.add(articleId);
            }
        });
        return ids;
    } catch (err) {
        console.warn('newsReadStateService.loadReadArticleIds failed:', err);
        return new Set();
    }
}

/**
 * Write read-state markers for `articleIds` under `userUid` in a single
 * Firestore batch. Idempotent — a re-write for an already-read article just
 * refreshes `read_at` / `expires_at`, which is fine (extends the TTL horizon).
 *
 * Batches larger than READ_STATE_BATCH_LIMIT are split into multiple commits
 * to respect Firestore's per-batch cap.
 *
 * Callers are expected to filter for "not already read" upstream so we don't
 * needlessly bump timestamps. On failure the promise resolves with
 * `{ committed: 0, error }` so the popover / hook layer can log without
 * throwing.
 */
export async function markArticlesReadBatch(userUid, articleIds) {
    const uid = normalizeUserUid(userUid);
    if (!uid) return { committed: 0, error: null };
    const seen = new Set();
    const cleaned = [];
    (Array.isArray(articleIds) ? articleIds : []).forEach((raw) => {
        const id = normalizeArticleId(raw);
        if (!id || seen.has(id)) return;
        seen.add(id);
        cleaned.push(id);
    });
    if (cleaned.length === 0) return { committed: 0, error: null };
    try {
        const nowMs = Date.now();
        const readAt = Timestamp.fromMillis(nowMs);
        const expiresAt = Timestamp.fromMillis(nowMs + READ_STATE_TTL_MS);
        let committed = 0;
        for (let start = 0; start < cleaned.length; start += READ_STATE_BATCH_LIMIT) {
            const chunk = cleaned.slice(start, start + READ_STATE_BATCH_LIMIT);
            const batch = writeBatch(firestore);
            chunk.forEach((articleId) => {
                const ref = doc(
                    firestore,
                    'users',
                    uid,
                    READ_STATE_COLLECTION,
                    articleId,
                );
                batch.set(ref, {
                    user_id: uid,
                    article_id: articleId,
                    read_at: readAt,
                    expires_at: expiresAt,
                });
            });
            // eslint-disable-next-line no-await-in-loop
            await batch.commit();
            committed += chunk.length;
        }
        return { committed, error: null };
    } catch (err) {
        console.warn('newsReadStateService.markArticlesReadBatch failed:', err);
        return { committed: 0, error: err };
    }
}
